/**
 * Dual-tone end labels for horizontal ranking bars: white value + green/red %.
 * chartjs-plugin-datalabels only paints one color per label, so we draw the
 * two segments ourselves after the bars.
 */

export const RANKING_VALUE_COLOR = "#ffffff";
export const RANKING_PCT_UP_COLOR = "#71BF8A";
export const RANKING_PCT_DOWN_COLOR = "#E74C3C";

export const formatRankingPercentChange = (pct: number): string => {
  const abs = Math.abs(pct).toFixed(1);
  return pct > 0 ? `+${abs}%` : pct < 0 ? `-${abs}%` : `${abs}%`;
};

export const rankingPercentChangeColor = (
  pct: number | null | undefined,
): string => {
  if (pct == null || !Number.isFinite(pct)) return RANKING_VALUE_COLOR;
  if (pct > 0) return RANKING_PCT_UP_COLOR;
  if (pct < 0) return RANKING_PCT_DOWN_COLOR;
  return RANKING_VALUE_COLOR;
};

export type RankingDatalabelParts = {
  valueText: string;
  percentText: string | null;
  percentColor: string;
};

export const buildRankingDatalabelParts = (
  valueLabel: string,
  pct: number | null | undefined,
): RankingDatalabelParts => ({
  valueText: valueLabel,
  percentText: pct == null ? null : formatRankingPercentChange(pct),
  percentColor: rankingPercentChangeColor(pct),
});

type GetPercentChange = (dataIndex: number) => number | null | undefined;
type FormatValue = (value: number) => string;

type RankingBarElement = {
  getProps: (keys: string[], final: boolean) => { x: number; y: number };
};

type RankingBarChart = {
  ctx: CanvasRenderingContext2D;
  data: { datasets: { data: unknown[] }[] };
  getDatasetMeta: (index: number) => { data?: RankingBarElement[] };
};

/**
 * Chart.js plugin: white main value, colored percent change to its right.
 */
export const createRankingBarDatalabelsPlugin = (
  getPercentChange: GetPercentChange,
  formatValue: FormatValue,
) => ({
  id: "rankingBarDatalabels",
  afterDatasetsDraw(chart: RankingBarChart) {
    const meta = chart.getDatasetMeta(0);
    const elements = meta?.data;
    if (!elements?.length) return;

    const ctx = chart.ctx;
    const dataset = chart.data.datasets[0]?.data ?? [];
    ctx.save();
    ctx.font = "600 11px Poppins-regular, sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";

    elements.forEach((element, dataIndex) => {
      const raw = Number(dataset[dataIndex]);
      if (!Number.isFinite(raw)) return;

      const { x, y } = element.getProps(["x", "y"], true);
      const parts = buildRankingDatalabelParts(
        formatValue(raw),
        getPercentChange(dataIndex),
      );

      let cursorX = x + 8;
      ctx.fillStyle = RANKING_VALUE_COLOR;
      ctx.fillText(parts.valueText, cursorX, y);

      if (parts.percentText) {
        cursorX += ctx.measureText(parts.valueText).width + 8;
        ctx.fillStyle = parts.percentColor;
        ctx.fillText(parts.percentText, cursorX, y);
      }
    });

    ctx.restore();
  },
});
